"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CatSagOptimizer = void 0;
const tslib_1 = require("tslib");
const fmin_1 = tslib_1.__importDefault(require("fmin"));
const multipoint_1 = require("./multipoint");
const vector_1 = require("./vector");
/** Defines a catenary sag curve of a wire given the start point, end point, and a single mid point along the wire. */
class CatSagOptimizer {
    /**
     * 2D Cartesian input points start, end, and mid that lie on the vertical plane containing the wire
     * IKE Field calls these P1, P2, and P3 respectively
     *
     * @param start start which is always 0,0 - Pole wire start point - will normally be an insulator
     * @param end Pole wire end point - will also normally be an insulator
     * @param mid Targeted point on the wire - Not necessarily at peak sag location
     */
    constructor(start, end, mid) {
        this.start = start;
        this.end = end;
        this.mid = mid;
        this.lineSlope = (this.end.y - this.start.y) / (this.end.x - this.start.x);
        this.optimizeCurve();
    }
    /** Create a sag optimizer from a collection of captures */
    static fromCaptures(captureStart, captureEnd, captureMid) {
        const sagPoints = multipoint_1.MultiPoint.getSagPoints2D(captureStart, captureEnd, captureMid);
        return new CatSagOptimizer(sagPoints.start, sagPoints.end, sagPoints.mid);
    }
    /**
     * Optimize the curve
     * Using the Nelder Mead minimization function to find the best fitting sag line
     * */
    optimizeCurve() {
        // Ensure `mid` is within bounds of `start` and `end`
        if (this.isOutOfBounds()) {
            return null;
        }
        // Ensure `mid` is below slope defined by `start` and `end`
        const sign = this.end.cross(this.mid).z;
        if (sign > 0) {
            // It is possible that a mid point measurement would be above the line from start to end
            // (e.g. if line is very taut, and measurement accuracy puts point slightly high.)
            // To deal with this, calculate a point on the straight line between start and end directly below
            // the midpoint
            this.mid.y = this.mid.x * this.lineSlope;
        }
        // Start with a default curve estimate - Larger values of 'a' lead to a flatter curve / taut cable - See Wikipedia: https://en.wikipedia.org/wiki/Catenary
        const aInitial = 1.0;
        // Initial guess for xOffset is midpoint between start and end
        const xOffsetInitial = -(this.end.x - this.start.x) / 2.0;
        // Initial guess for yOffset is height between startpoint and midpoint measurement
        const yOffsetInitial = this.start.y - this.mid.y;
        const x0 = [aInitial, xOffsetInitial, yOffsetInitial];
        const result = fmin_1.default.nelderMead(this.minimizeFuncNelder.bind(this), x0);
        if (result.fx > 0.001) {
            // Failed to converge
            return null;
        }
        this.a = result.x[0];
        this.xOffset = result.x[1];
        this.yOffset = result.x[2];
    }
    /**
     * Check the input points are valid
     * Because of the way coordinates are generated from the 3d points in world-space
     * the end point should always be further from the origin than the start point(+ direction)
     *
     * The mid-point needs to be between the start and end points, along the x axis
     *
     * @return {boolean} true if the given coordinates are invalid
     * */
    isOutOfBounds() {
        if (this.start.x > this.end.x) {
            return true;
        }
        if (this.mid.x < this.start.x || this.mid.x > this.end.x) {
            return true;
        }
        return false;
    }
    /**
     * Return the wire length including sag
     * Calculate the full length of the wire including sag - Denoted as s in Wikipedia article
     * https://en.wikipedia.org/wiki/Catenary#Determining_parameters
     *
     * @returns Wire length (m)
     */
    getLength() {
        if (this.isOutOfBounds()) {
            return null;
        }
        // returns negative value if the end is end has a smaller x value than the start.
        const s = this.a * Math.sinh((this.end.x + this.xOffset) / this.a) - this.a * Math.sinh((this.start.x + this.xOffset) / this.a);
        // Length is always positive
        return Math.abs(s);
    }
    /**
     * Find the maximal sag
     *
     * @typedef {Object} SagResult
     * @property {number} maxSag Maximal sag (m)
     * @property {number} maxX Maximal x offset (m)
     * @property {number} maxY Maximal y offset (m)
     * @property {number} maxSlopeY Maximal slope (??)
     *
     * @return {SagResult} Maximal sag result
     */
    getSag() {
        if (this.isOutOfBounds()) {
            return null;
        }
        // Maximize the sag value using Nelder Mead.
        // Starting from the center of the wire.
        const midX = (this.start.x + this.end.x) / 2;
        const x0 = [midX];
        const result = fmin_1.default.nelderMead(this.maximizeFuncNelder.bind(this), x0);
        const [sagX] = result.x;
        // Result out of bounds
        if (sagX < this.start.x || sagX > this.end.x) {
            return null;
        }
        return this.getSagResult(result.x[0]);
    }
    /**
     * Get ths sag result given a specific X
     * @param x X offset to calculate for
     */
    getSagResult(x) {
        let slopeY = x * this.lineSlope;
        let catY = this.getCurveHeight(this.a, x + this.xOffset) - this.yOffset;
        return {
            maxSag: slopeY - catY,
            maxX: x,
            maxY: catY,
            maxSlopeY: slopeY
        };
    }
    /**
     * Maximize the sag height
     *
     * To maximize the sag, we invert the sag result as the nelder mead expects to minimize the sag.
     *
     * @param args xOffset to calculate the sag
     **/
    maximizeFuncNelder(args) {
        const [x] = args;
        // Out of bounds no correct answer
        if (x < this.start.x || x > this.end.x) {
            return 0;
        }
        const slopeY = x * this.lineSlope;
        const catY = this.getCurveHeight(this.a, x + this.xOffset) - this.yOffset;
        const sag = slopeY - catY;
        // NelderMead is looking for a minimum so convert this negative
        // so that it will converge on the correct point
        return -1 * sag;
    }
    /**
     * Minimization function.
     *
     * This function is called repeatedly until the minimal distance to mid is found.
     **/
    minimizeFuncNelder(args) {
        const [a, xOffset, yOffset] = args;
        return this.calcCurveError(a, xOffset, yOffset);
    }
    /**
     * Find the height of the sag at position x offset from start.x
     * @param a Curve factor for the cosh function - Indicates how much sag the wire has. Larger a => flatter curve / more taut wire
     * @param x X position to find the height for
     * @returns Height of sag in m
     */
    getCurveHeight(a, x) {
        return a * Math.cosh(x / a) - a;
    }
    /**
     * Calculate the overall RMS error between the start, mid, and end points and the resulting catenary curve
     * when using the given curve factor (a), and 2D offset (xOffset, yOffset)
     * @param a
     * @param xOffset
     * @param yOffset
     */
    calcCurveError(a, xOffset, yOffset) {
        const MAX_ERROR = 100000000;
        if (a <= 0.0 || xOffset > 0) {
            return MAX_ERROR;
        }
        const offset = new vector_1.Vector(xOffset, yOffset);
        const transformedStart = this.start.add(offset);
        const transformedMid = this.mid.add(offset);
        const transformedEnd = this.end.add(offset);
        const calculatedStartY = this.getCurveHeight(a, transformedStart.x);
        const calculatedMidY = this.getCurveHeight(a, transformedMid.x);
        const calculatedEndY = this.getCurveHeight(a, transformedEnd.x);
        const rmsError = Math.sqrt(Math.pow(calculatedStartY - transformedStart.y, 2) +
            Math.pow(calculatedMidY - transformedMid.y, 2) +
            Math.pow(calculatedEndY - transformedEnd.y, 2));
        return rmsError;
    }
}
exports.CatSagOptimizer = CatSagOptimizer;
//# sourceMappingURL=catenary.js.map